import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { CustomLabelInput } from '../../../../../components/input/CustomLabelInput';
import CustomBlockButton from '../../../../../components/button/CustomBlockButton';
import { ModalValueTypeChange } from './modals/modal-value-type-change';
import { ModalMergeable } from './modals/modal-mergeable';
import { ModalFieldNames } from './modals/modal-field-names';
import { useQueryParamsHook } from '../../../../../utils/useQueryParamsHook';
import { useNavigate } from 'react-router-dom';
import { ReturnExchangeDownloadFormsApi } from '../../../../../apis/ReturnExchangeDownloadFormsApi';
import { SellertoolSignatureUtils } from '../../../../../utils/sellertool-signature-utils';
import { useApiInfoContextValueHook } from '../../../../../contexts/ApiInfoContext';
import { useReturnExchangeDownloadFormsValueContextHook } from '../../contexts/ReturnExchangeDownloadFormsContext';
import { useFetcher } from '../../fetcher/useFetcher';
import { CustomBackdropController } from '../../../../../components/backdrop/default/v1';
import { customToast } from '../../../../../components/toast/custom-react-toastify/v1';

const StyledContainer = styled.div`
    padding: 10px;
`;

const StyledTitle = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;

    input{
        border: 1px solid #e0e0e0;
        border-radius: 3px;
        background: #fff;
    }
`;

const StyledAddItemButton = styled.div`
    margin-top: 20px;
    button{
        width: 100px;
        border-color: #333;
        border-radius: 3px;
        color: #333;
        font-weight: 600;
    }
`;

const StyledItemList = styled.div`
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const StyledItem = styled.div`
    background: #fff;
    border: 1px solid #e0e0e0;
    padding: 10px;
    border-radius: 3px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .contents{
        display: flex;
        gap: 10px;
        align-items: center;
    }

    .cellName{
        & input{
            border: 1px solid #e0e0e0;
            border-radius: 3px;
            box-sizing: border-box;
            width: 200px;
        }
    }

    .valueType{
        button{
            width: 100px;
            border-color: #333;
            border-radius: 3px;
            color: #333;
            font-weight: 600;
        }
    }

    .fieldNames{
        button{
            width: 100px;
            border-color: #333;
            border-radius: 3px;
            color: #333;
            font-weight: 600;
        }
    }

    .fixedValue{
        & input{
            border: 1px solid #e0e0e0;
            border-radius: 3px;
            box-sizing: border-box;
            width: 200px;
        }
    }

    .mergeable{
        button{
            width: 100px;
            border-color: #333;
            border-radius: 3px;
            color: #333;
            font-weight: 600;

            &.on{
                border-color: var(--greenColor1);
                color: var(--greenColor1);
            }

            &.off{
                border-color: var(--redColor1);
                color: var(--redColor1);
            }
        }
    }

    .removeItem{
        button{
            width: 70px;
            border:none;
            background: var(--redColor1);
            border-radius: 3px;
            color: #fff;
            font-weight: 600;
        }
    }
`;

const StyledFloatingButton = styled.div`
    position: fixed;
    bottom: 20px;
    right: 20px;
    display: flex;
    gap: 10px;
    button{
        width: 100px;
        border:none;
        background: var(--blueColor1);
        border-radius: 3px;
        color: #fff;
        font-weight: 600;

        &.cancel{
            background: var(--redColor1);
        }
    }
`;

export function WegetEdit(props) {
    const apiInfoContextValueHook = useApiInfoContextValueHook();
    const queryParamsHook = useQueryParamsHook();
    const returnExchangeDownloadFormId = queryParamsHook?.params?.returnExchangeDownloadFormId || null;
    const returnExchangeDownloadFormsValueContextHook = useReturnExchangeDownloadFormsValueContextHook();
    const returnExchangeDownloadForms = returnExchangeDownloadFormsValueContextHook?.returnExchangeDownloadForms;
    const navigate = useNavigate();
    const fetcher = useFetcher();

    if (!returnExchangeDownloadFormId) {
        alert('잘못된 접근 방식 입니다.')
        navigate(-1);
    }

    const [formDatas, setFormDatas] = useState(null);
    const [valueTypeChangeModalOpen, setValueTypeChangeModalOpen] = useState(false);
    const [mergeableModalOpen, setMergeableModalOpen] = useState(false);
    const [fieldNamesModalOpen, setFieldNamesModalOpen] = useState(false);

    const [targetItem, setTargetItem] = useState(null);

    useEffect(() => {
        if (!returnExchangeDownloadForms) {
            return;
        }

        setFormDatas(returnExchangeDownloadForms.find(item => item._id === returnExchangeDownloadFormId));
    }, [returnExchangeDownloadForms]);

    const handleCancel = () => {
        navigate(-1);
    }

    const handleSubmit = async () => {
        const timestamp = Date.now().toString();
        const signature = SellertoolSignatureUtils().generateSigniture({
            apiKey: apiInfoContextValueHook?.apiInfo?.apiKey,
            secretKey: apiInfoContextValueHook?.apiInfo?.secretKey,
            timestamp: timestamp
        })

        const headers = {
            'x-sellertool-apiKey': apiInfoContextValueHook?.apiInfo?.apiKey,
            'x-sellertool-timestamp': timestamp,
            'x-sellertool-signiture': signature
        }

        const body = {
            formDatas: [formDatas]
        }
        CustomBackdropController().showBackdrop();
        const response = await ReturnExchangeDownloadFormsApi().updateList({ headers: headers, body: body })

        if (response?.content) {
            navigate(-1);
            fetcher.fetchReturnExchangeDownloadForms();
        } else {
            customToast.warn('수정 내용이 없습니다.');
        }

        CustomBackdropController().hideBackdrop();
    }

    const handleAddItem = () => {
        setFormDatas(prev => {
            return {
                ...prev,
                items: [
                    ...prev.items,
                    {
                        uuid: uuidv4(),
                        cellName: `컬럼 ${prev.items?.length + 1}`,
                        fieldNames: [],
                        fixedValue: '',
                        valueType: 'NORMAL', // NORMAL, FREIGHT_CODE, FIXED,
                        mergeableFlag: false,
                        valueSeparator: '-',
                        mergeSeparator: '\n',
                    }
                ]
            }
        })
    }

    const handleChangeFormName = (e) => {
        setFormDatas(prev => {
            return {
                ...prev,
                name: e.target.value
            }
        })
    }

    const handleChangeFormDescription = (e) => {
        setFormDatas(prev => {
            return {
                ...prev,
                description: e.target.value
            }
        })
    }

    const handleChangeCellName = (e, item) => {
        setFormDatas(prev => {
            return {
                ...prev,
                items: prev.items.map(i => {
                    if (i.uuid === item.uuid) {
                        return {
                            ...i,
                            cellName: e.target.value
                        }
                    } else {
                        return i;
                    }
                })
            }
        });
    }

    const handleChangeFixedValue = (e, item) => {
        setFormDatas(prev => {
            return {
                ...prev,
                items: prev.items.map(i => {
                    if (i.uuid === item.uuid) {
                        return {
                            ...i,
                            fixedValue: e.target.value
                        }
                    } else {
                        return i;
                    }
                })
            }
        });
    }

    const handleValueTypeChangeModalOpem = (item) => {
        setValueTypeChangeModalOpen(true);
        setTargetItem(item);
    }

    const handleValueTypeChangeModalClose = (item) => {
        setValueTypeChangeModalOpen(false);
        setTargetItem(null);
    }

    const handleChangeValueType = (valueType) => {
        setFormDatas(prev => {
            return {
                ...prev,
                items: prev.items.map(item => {
                    if (item.uuid === targetItem.uuid) {
                        return {
                            ...item,
                            valueType: valueType,
                        }
                    } else {
                        return item;
                    }
                })
            }
        });
        handleValueTypeChangeModalClose();
    }

    const handleMergeableModalOpen = (item) => {
        setMergeableModalOpen(true);
        setTargetItem(item);
    }

    const handleMergeableModalClose = () => {
        setMergeableModalOpen(false);
        setTargetItem(null);
    }

    const handleSetNewItem = (newItem) => {
        setFormDatas(prev => {
            return {
                ...prev,
                items: prev.items.map(item => {
                    if (item.uuid === newItem.uuid) {
                        return newItem;
                    } else {
                        return item;
                    }
                })
            }
        });
    }

    const handleFieldNamesModalOpen = (item) => {
        setFieldNamesModalOpen(true);
        setTargetItem(item);
    }

    const handleFieldNamesModalClose = () => {
        setFieldNamesModalOpen(false);
        setTargetItem(null);
    }

    const handleRemoveItem = (item) => {
        setFormDatas(prev => {
            return {
                ...prev,
                items: prev.items.filter(i => i.uuid !== item.uuid)
            }
        });
    }

    if (!formDatas) {
        return (
            <StyledContainer>
                <div >로딩중...</div>
            </StyledContainer>
        )
    }
    return (
        <>
            <StyledContainer>
                <StyledTitle>
                    <CustomLabelInput>
                        <CustomLabelInput.Input
                            type='text'
                            value={formDatas?.name || ''}
                            onChange={handleChangeFormName}
                        />
                        <CustomLabelInput.Label>다운로드 폼 이름</CustomLabelInput.Label>
                    </CustomLabelInput>
                    <CustomLabelInput>
                        <CustomLabelInput.Input
                            type='text'
                            value={formDatas?.description || ''}
                            onChange={handleChangeFormDescription}
                        />
                        <CustomLabelInput.Label>다운로드 폼 설명</CustomLabelInput.Label>
                    </CustomLabelInput>
                </StyledTitle>
                <StyledAddItemButton>
                    <CustomBlockButton onClick={handleAddItem}>항목 추가</CustomBlockButton>
                </StyledAddItemButton>
                <StyledItemList>
                    {formDatas.items.map((item, index) => {
                        return (
                            <StyledItem key={item.uuid}>
                                <div className='contents'>
                                    <div>{index + 1}</div>
                                    <div className='cellName'>
                                        <CustomLabelInput>
                                            <CustomLabelInput.Input type='text' value={item?.cellName || ''} onChange={(e) => handleChangeCellName(e, item)} />
                                            <CustomLabelInput.Label>셀 이름</CustomLabelInput.Label>
                                        </CustomLabelInput>
                                    </div>
                                    <div className='valueType'>
                                        <CustomBlockButton
                                            type='button'
                                            onClick={() => handleValueTypeChangeModalOpem(item)}
                                        >
                                            {item?.valueType === 'NORMAL' && '일반'}
                                            {item?.valueType === 'FIXED' && '고정값'}
                                            {item?.valueType === 'FREIGHT_CODE' && '운송코드'}
                                        </CustomBlockButton>
                                    </div>
                                    {item?.valueType === 'NORMAL' &&
                                        <div className='fieldNames'>
                                            <CustomBlockButton
                                                type='button'
                                                onClick={() => handleFieldNamesModalOpen(item)}
                                            >필드값 ({item?.fieldNames?.length})</CustomBlockButton>
                                        </div>
                                    }
                                    {item?.valueType === 'FIXED' &&
                                        <div className='fixedValue'>
                                            <CustomLabelInput>
                                                <CustomLabelInput.Input type='text' value={item?.fixedValue || ''} onChange={(e) => handleChangeFixedValue(e, item)} />
                                                <CustomLabelInput.Label>고정값</CustomLabelInput.Label>
                                            </CustomLabelInput>
                                        </div>
                                    }
                                    {item?.valueType !== 'FREIGHT_CODE' &&
                                        <div className='mergeable'>
                                            <CustomBlockButton
                                                type='button'
                                                onClick={() => handleMergeableModalOpen(item)}
                                                className={`${item?.mergeableFlag ? 'on' : 'off'}`}
                                            >
                                                {item?.mergeableFlag ? '병합 사용' : '병합 사용안함'}
                                            </CustomBlockButton>
                                        </div>
                                    }
                                </div>
                                <div className='removeItem'>
                                    <CustomBlockButton
                                        type='button'
                                        onClick={() => handleRemoveItem(item)}
                                    >
                                        제거
                                    </CustomBlockButton>
                                </div>
                            </StyledItem>
                        )
                    })}
                </StyledItemList>
            </StyledContainer>
            <StyledFloatingButton>
                <CustomBlockButton
                    type='button'
                    className='cancel'
                    onClick={() => handleCancel()}
                >
                    취소
                </CustomBlockButton>
                <CustomBlockButton
                    type='button'
                    className='confirm'
                    onClick={() => handleSubmit()}
                >
                    수정
                </CustomBlockButton>
            </StyledFloatingButton>

            {valueTypeChangeModalOpen &&
                <ModalValueTypeChange
                    open={valueTypeChangeModalOpen}
                    onClose={handleValueTypeChangeModalClose}
                    onSelect={handleChangeValueType}
                />
            }
            {(mergeableModalOpen && targetItem) &&
                <ModalMergeable
                    open={mergeableModalOpen}
                    onClose={handleMergeableModalClose}
                    targetItem={targetItem}
                    onConfirm={handleSetNewItem}
                />
            }

            {(fieldNamesModalOpen && targetItem) &&
                <ModalFieldNames
                    open={fieldNamesModalOpen}
                    onClose={handleFieldNamesModalClose}
                    targetItem={targetItem}
                    onConfirm={handleSetNewItem}
                />
            }
        </>
    );
}